import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from "@angular/core";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { debounceTime, map, take, takeUntil } from "rxjs/operators";

export interface SelectBoxSelfFetchState {
	[key: string]: any;
	page: number;
	size: number;
	search?: string;
}

export interface SelfFetchOption<T> {
	getInitialState: () => SelectBoxSelfFetchState;
	nextChunk: (state: SelectBoxSelfFetchState) => SelectBoxSelfFetchState; // if search exist, it must be considered
	getData$?: (state: SelectBoxSelfFetchState) => Observable<T>;
	mapData?: (data: T) => [any, string][];
}

@Component({
	selector: "app-select-list",
	templateUrl: "./select-list.component.html",
	styleUrls: ["./select-list.component.scss"]
})
export class SelectListComponent<T> implements OnDestroy, AfterViewInit, OnChanges, OnInit {
	@Input("option") public selfFetchOption: SelfFetchOption<T>;
	@Input() public clearDefault = false;
	@Input() public showSearch = true;
	@Input() public isDialog = true;
	@Output() public selectValueChange: EventEmitter<any> = new EventEmitter<any>(); // TODO model
	public data: [any, string][] = [];
	public presetData: [any, string][] = [];
	public value: any | null = null;
	public selected?: [any, string]; //todo: refactor selected
	public input$$: Subject<string> = new Subject<string>();
	public isLoadData = false;
	private initialState: SelectBoxSelfFetchState = { page: 1, size: 30 };
	private state$$: BehaviorSubject<SelectBoxSelfFetchState>;
	private stateTracker$$?: Subject<void>;

	protected unsubscribe$$: Subject<void> = new Subject<void>();

	private defaultOption: SelfFetchOption<T> = {
		getInitialState: (): SelectBoxSelfFetchState => ({ ...this.initialState }),
		nextChunk: (state): SelectBoxSelfFetchState => ({ ...state, page: state.page + 1 })
	};

	constructor(private changeDetectorRef: ChangeDetectorRef) {
		this.selfFetchOption = { ...this.defaultOption };
		this.state$$ = new BehaviorSubject<SelectBoxSelfFetchState>(this.selfFetchOption.getInitialState());
		this.input$$.pipe(
			debounceTime(250),
			takeUntil(this.unsubscribe$$)
		).subscribe(text => this.onInput(text));
	}

	public ngOnInit(): void {
		this.state$$.next(this.selfFetchOption.getInitialState());
		this.stateTracker$$ = this.trackState$$();
	}

	public ngAfterViewInit(): void {
		this.setData(this.data);
		this.writeValue(this.value);
		this.changeDetectorRef.markForCheck();
	}

	public writeValue(value: any): void {
		if (value === undefined) {
			value = null;
		}
		this.value = value;
		this.selected = this.data.find(e => e[0] === value) || value && this.transform(value);
	}

	public ngOnChanges(simpleChanges: SimpleChanges): void {
		if (simpleChanges.selfFetchOption && simpleChanges.selfFetchOption.currentValue) {
			// if not pass required function use default
			this.selfFetchOption = { ...this.defaultOption, ...this.selfFetchOption };
		}
	}

	public onInput(search: string): void {
		this.resetData();
		this.cleanStateTracker();
		const newState = { ...this.selfFetchOption.getInitialState(), search };
		this.state$$.next(newState);
		this.stateTracker$$ = this.trackState$$();
	}

	private trackState$$(): Subject<void> {
		const unsubscribe$$ = new Subject<void>();
		this.state$$.pipe(
			takeUntil(this.unsubscribe$$),
			takeUntil(unsubscribe$$)
		).subscribe(state => {
			if (!this.selfFetchOption.getData$)
				throw Error("No getData$ using for enrich date");

			this.selfFetchOption.getData$(state).pipe(
				take(1),
				map(data => this.selfFetchOption.mapData ? this.selfFetchOption.mapData(data) : data as any),
				takeUntil(this.unsubscribe$$),
				takeUntil(unsubscribe$$)
			).subscribe(data => {
				if (Array.isArray(data) && !data.length) {
					unsubscribe$$.next();
					unsubscribe$$.complete();
				}
				this.isLoadData = false;
				this.appendData(data);
				this.changeDetectorRef.detectChanges();
			},
			() => {
				this.isLoadData = false;
				this.cleanStateTracker();
				this.changeDetectorRef.detectChanges();
			});
		});
		return unsubscribe$$;
	}

	public ngOnDestroy(): void {
		this.cleanStateTracker();
		this.unsubscribe$$.next();
		this.unsubscribe$$.complete();
	}

	private cleanStateTracker(): void {
		if (this.stateTracker$$) {
			this.stateTracker$$.next();
			this.stateTracker$$.complete();
			delete this.stateTracker$$;
		}
	}

	public select(item: [any, string]): void {
		this.selectValueChange.emit(item);
		this.selected = item.slice(0) as [any, string]; // new instance !
		this.changeDetectorRef.markForCheck();
	}

	public onScrolled(): void {
		this.state$$.next(this.selfFetchOption.nextChunk(this.state$$.value));
	}

	private transform(value: any): [any, string] {
		if (this.selfFetchOption.mapData) {
			const data = this.selfFetchOption.mapData([value] as any as T);
			if (data.length)
				return data[0];
		}
		return [value, value]; // todo: write call transform fn here
	}

	private resetData(): void {
		this.data = this.clearDefault ? [] : [...this.presetData];
	}

	private appendData(newData: [any, string][]): void {
		this.data = [...this.data, ...newData];
	}

	private setData(newData: [any, string][]): void {
		this.data = this.clearDefault ? [...newData] : [...this.presetData, ...newData];
	}
}
